import { useEffect } from 'react'

import { clearStoredHomebaseAnalyticsTreatmentOption } from '../../homebaseAnalyticsAttribution/treatmentOptionStorage'
import { clearStorageDataOnLogout } from '../utils/logoutUtils'

/**
 * The name of the BroadcastChannel used for session synchronization across tabs/windows.
 */
const SESSION_SYNC_CHANNEL = 'session-sync-channel'

/**
 * The message type sent when a session is terminated and should be synchronized across tabs/windows.
 */
export const SESSION_TERMINATED_MESSAGE = 'SESSION_TERMINATED'

/**
 * The message type sent when a session is timed out due to inactivity and should be synchronized across tabs/windows.
 */
const SESSION_TIMED_OUT_MESSAGE = 'SESSION_TIMED_OUT'

/**
 * The message type sent after a successful account transfer so other tabs can
 * invalidate account-scoped client state without ending the shared session.
 */
export const ACCOUNT_TRANSFERRED_MESSAGE = 'ACCOUNT_TRANSFERRED'

export interface UseSessionSyncConfig {
  /**
   * Function to dispatch actions
   */
  dispatch: (action: any) => any
  /**
   * Function to get the current logged-in state
   */
  getIsLoggedIn: () => boolean
  /**
   * Action to dispatch when session expires
   */
  sessionExpiredLogoutAction: () => any
  /**
   * Action to dispatch when session times out
   */
  setIsSessionTimedOutAction: (value: boolean) => any

  /**
   * Optional callback when session times out for 1Lyra page
   */
  handle1LyraSessionExpired?: () => void

  /**
   * Optional callback used to invalidate account-scoped state after another
   * tab successfully transfers the shared session to a different account.
   */
  handleAccountTransferred?: () => void

  /**
   * Enables session sync across tabs
   * Defaults to true; set to false for native mobile
   */
  isEnabled?: boolean
}

/**
 * A React hook that synchronizes session state across multiple tabs/windows.
 *
 * This hook listens for session termination messages from other tabs/windows
 * via a BroadcastChannel. When a SESSION_TERMINATED_MESSAGE is received and
 * the user is logged in, it calls the onSessionTerminated callback or redirects
 * the current tab to the logout page.
 *
 * This ensures that when a user logs out in one tab or their session expires,
 * all other open tabs of the application will also log out, maintaining session
 * consistency across the entire browser.
 *
 * @param config - Configuration object with dispatch, selectors, and actions
 */

export const useSessionSync = (config: UseSessionSyncConfig) => {
  const {
    dispatch,
    getIsLoggedIn,
    sessionExpiredLogoutAction,
    setIsSessionTimedOutAction,
    handle1LyraSessionExpired,
    handleAccountTransferred,
    isEnabled = true,
  } = config

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const channel = new BroadcastChannel(SESSION_SYNC_CHANNEL)
    const handleSessionMessage = (event: MessageEvent) => {
      if (event.data.type === ACCOUNT_TRANSFERRED_MESSAGE) {
        // Legacy and 1Lyra tabs share this sessionStorage key. Clear it in the
        // shared listener even when an app has no in-memory invalidation hook.
        clearStoredHomebaseAnalyticsTreatmentOption()
        handleAccountTransferred?.()
        return
      }

      const isLoggedIn = getIsLoggedIn()
      if (isLoggedIn) {
        if (event.data.type === SESSION_TIMED_OUT_MESSAGE) {
          if (handle1LyraSessionExpired) {
            handle1LyraSessionExpired()
          } else {
            // Used for web app only
            dispatch(sessionExpiredLogoutAction())
            dispatch(setIsSessionTimedOutAction(true))
          }
        } else if (event.data.type === SESSION_TERMINATED_MESSAGE) {
          // sessionStorage is tab-scoped, so each receiving tab must clear its
          // own analytics mirror before leaving the authenticated session.
          clearStorageDataOnLogout()
          window.location.href = '/logout'
        }
      }
    }

    channel.onmessage = handleSessionMessage

    return () => {
      channel.close()
    }
  }, [
    dispatch,
    getIsLoggedIn,
    sessionExpiredLogoutAction,
    setIsSessionTimedOutAction,
    isEnabled,
    handle1LyraSessionExpired,
    handleAccountTransferred,
  ])
}

/**
 * Broadcasts a session termination message to all open tabs/windows of the application.
 *
 * This function creates a BroadcastChannel using the SESSION_SYNC_CHANNEL constant,
 * sends a SESSION_TERMINATED_MESSAGE to notify all other tabs that the session has been
 * terminated, and then closes the channel. When other tabs receive this message via
 * the useSessionSync hook, they will redirect to the logout page if the user is logged in.
 *
 * Use this function when you need to terminate the user's session across all open instances
 * of the application, such as during logout, session timeout, or authentication failures.
 *
 * @param isSessionTimedOut - Whether the session timed out (defaults to false)
 */
export const broadcastSessionTermination = (isSessionTimedOut = false) => {
  const channel = new BroadcastChannel(SESSION_SYNC_CHANNEL)
  channel.postMessage({ type: isSessionTimedOut ? SESSION_TIMED_OUT_MESSAGE : SESSION_TERMINATED_MESSAGE })
  channel.close()
}

/**
 * Notifies other tabs that the shared browser session now belongs to a
 * different account. Each tab is responsible for clearing its account-scoped
 * in-memory state when it receives this message.
 */
export const broadcastAccountTransfer = () => {
  const channel = new BroadcastChannel(SESSION_SYNC_CHANNEL)
  channel.postMessage({ type: ACCOUNT_TRANSFERRED_MESSAGE })
  channel.close()
}
